import { gql, useLazyQuery, useMutation } from '@apollo/client';
import { Dialog, DialogContent, DialogTitle } from '@mui/material';
import dayjs from 'dayjs';
import { FC, useEffect, useState } from 'react';

import Columns from 'components/Columns';
import ErrorMessage from 'components/ErrorMessage';
import { FormInput, FormPanel } from 'components/FormPanel';
import LoadingIndicator from 'components/LoadingIndicator';

import countries from 'utils/countries-enum.json';

const GET_USER_QUERY = gql`
  query UserUpsert($id: ID!) {
    user(id: $id) {
      id
      department {
        id
        name
      }
      firstName
      lastName
      fullName
      email
      gender
      dateOfBirth
      nationality
      role
    }
  }
`;

const UPSERT_USER_MUTATION = gql`
  mutation UpsertUserInfo(
    $id: ID
    $departmentId: ID!
    $firstName: String!
    $lastName: String!
    $email: String!
    $password: String
    $gender: GenderEnumType!
    $dateOfBirth: String!
    $nationality: NationalityEnumType!
    $role: RoleEnumType!
  ) {
    upsertUser(
      id: $id
      departmentId: $departmentId
      firstName: $firstName
      lastName: $lastName
      email: $email
      password: $password
      gender: $gender
      dateOfBirth: $dateOfBirth
      nationality: $nationality
      role: $role
    ) {
      id
      department {
        id
        name
      }
      firstName
      lastName
      fullName
      email
      gender
      dateOfBirth
      nationality
      role
    }
  }
`;

const UpsertUserDialogButton: FC<{
  userId?: string;
  onClose: () => void;
}> = ({ userId, onClose }) => {
  const [showDialog, toggleDialog] = useState(false);

  const [getUser, { data, loading, error, refetch }] = useLazyQuery(GET_USER_QUERY);

  const [upsertUser, { loading: upsertLoading, error: upsertError }] = useMutation(
    UPSERT_USER_MUTATION,
    {
      refetchQueries: ['AdministrationUsers'],
    }
  );

  useEffect(() => {
    if (!userId) return;

    toggleDialog(true);
    getUser({ variables: { id: userId } });
  }, [userId, getUser]);

  function renderContent() {
    if (loading) return <LoadingIndicator />;

    if (error) return <ErrorMessage error={error} refetch={refetch} />;

    if (userId && !data) return <LoadingIndicator />;

    return (
      <FormPanel
        loading={upsertLoading}
        error={upsertError}
        onSubmit={data => {
          upsertUser({
            variables: {
              id: userId ? userId : undefined,
              departmentId: 'bc1d6dd9-f1cf-4d91-a7aa-98b8cf6bad4f',
              firstName: data.firstName,
              lastName: data.lastName,
              email: data.email,
              password: data.password,
              gender: data.gender,
              dateOfBirth: dayjs(data.dateOfBirth).toISOString(),
              nationality: data.nationality,
              role: data.role,
            },
          }).then(data => {
            onClose();
            toggleDialog(false);
          });
        }}
        submitButtonLabel="Save and continue"
      >
        <FormInput
          fullWidth
          type="string"
          fieldName="email"
          label="Email"
          defaultValue={data?.user.email || ''}
          validators={{
            required: true,
            isEmail: true,
          }}
        />
        <Columns number={2}>
          <FormInput
            fullWidth
            type="string"
            fieldName="firstName"
            label="First Name"
            defaultValue={data?.user.firstName || ''}
            validators={{
              required: true,
            }}
          />
          <FormInput
            fullWidth
            type="string"
            fieldName="lastName"
            label="Last Name"
            defaultValue={data?.user.lastName || ''}
            validators={{
              required: true,
            }}
          />
        </Columns>
        <Columns number={2}>
          <FormInput
            fullWidth
            type="select"
            fieldName="gender"
            label="Gender"
            defaultValue={data?.user.gender || ''}
            options={[
              { value: 'MALE', label: 'Male' },
              { value: 'FEMALE', label: 'Female' },
              { value: 'OTHER', label: 'Other' },
            ]}
            validators={{
              required: true,
            }}
          />
          <FormInput
            fullWidth
            type="date"
            fieldName="dateOfBirth"
            label="Date of Birth"
            defaultValue={data?.user.dateOfBirth || ''}
            validators={{
              required: true,
            }}
          />
        </Columns>
        <Columns number={2}>
          <FormInput
            fullWidth
            type="select"
            fieldName="nationality"
            label="Nationality"
            defaultValue={data?.user.nationality || 'PAPUA_NEW_GUINEA'}
            options={
              Object.keys(countries).map(key => ({
                value: key,
                label: countries[key],
              })) as { value: string; label: string }[]
            }
            validators={{
              required: true,
            }}
          />
          <FormInput
            fullWidth
            type="select"
            fieldName="role"
            label="Role"
            helperText="The role here refers to the permission roles of this dashboard not the person's organisation"
            defaultValue={data?.user.role || 'SUPER_ADMIN'}
            editable={false}
            options={[
              { value: 'EMPLOYEE', label: 'Employee' },
              { value: 'ADMIN', label: 'Admin' },
              { value: 'SUPER_ADMIN', label: 'Super Admin' },
            ]}
            validators={{
              required: true,
            }}
          />
        </Columns>
        <FormInput
          fullWidth
          type="string"
          fieldName="password"
          label="Password"
          helperText="Leave this field empty if you do not want to change the password"
          defaultValue={''}
          validators={{
            required: true,
          }}
        />
      </FormPanel>
    );
  }

  return (
    <>
      <Dialog
        open={showDialog}
        onClose={() => {
          onClose();
          toggleDialog(false);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Add Super Admin User</DialogTitle>
        <DialogContent sx={{ minHeight: '50vh' }}>{renderContent()}</DialogContent>
      </Dialog>
    </>
  );
};

export default UpsertUserDialogButton;
